import React, { useMemo } from 'react';

import Button from '../../../components/ui/Button';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths } from 'date-fns';

const BillCalendar = ({ bills, selectedMonth, onMonthChange, onBillClick }) => {
  const monthStart = startOfMonth(selectedMonth);
  const monthEnd = endOfMonth(selectedMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const billsByDate = useMemo(() => {
    const billMap = {};
    bills?.forEach(bill => {
      const dateKey = format(new Date(bill?.next_due_date), 'yyyy-MM-dd');
      if (!billMap?.[dateKey]) {
        billMap[dateKey] = [];
      }
      billMap?.[dateKey]?.push(bill);
    });
    return billMap;
  }, [bills]);

  const getCategoryColor = (category) => {
    const colors = {
      utilities: 'bg-blue-500',
      transportation: 'bg-green-500',
      groceries: 'bg-yellow-500',
      entertainment: 'bg-purple-500',
      healthcare: 'bg-red-500',
      dining: 'bg-orange-500',
      shopping: 'bg-pink-500',
      housing: 'bg-indigo-500',
      insurance: 'bg-teal-500',
      education: 'bg-cyan-500',
      other: 'bg-gray-500'
    };
    return colors?.[category] || 'bg-gray-500';
  };

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 border shadow-sm">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">
          {format(selectedMonth, 'MMMM yyyy')}
        </h2>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => onMonthChange(subMonths(selectedMonth, 1))}
            variant="outline"
            size="sm"
            iconName="ChevronLeft"
          />
          <Button
            onClick={() => onMonthChange(new Date())}
            variant="outline"
            size="sm"
          >
            Today
          </Button>
          <Button
            onClick={() => onMonthChange(addMonths(selectedMonth, 1))}
            variant="outline"
            size="sm"
            iconName="ChevronRight"
          />
        </div>
      </div>

      {/* Weekday Headers */}
      <div className="grid grid-cols-7 gap-2 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']?.map(day => (
          <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-2">
        {daysInMonth?.map(day => {
          const dateKey = format(day, 'yyyy-MM-dd');
          const dayBills = billsByDate?.[dateKey] || [];
          const isToday = isSameDay(day, new Date());

          return (
            <div
              key={dateKey}
              className={`min-h-[100px] p-2 rounded-lg border transition-all ${
                isToday ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
              } ${
                !isSameMonth(day, selectedMonth) ? 'opacity-50' : ''
              }`}
            >
              <div className={`text-sm font-medium mb-2 ${
                isToday ? 'text-primary' : 'text-foreground'
              }`}>
                {format(day, 'd')}
              </div>
              <div className="space-y-1">
                {dayBills?.slice(0, 3)?.map(bill => (
                  <button
                    key={bill?.id}
                    onClick={() => onBillClick(bill)}
                    className={`w-full text-left px-2 py-1 rounded text-xs font-medium text-white truncate ${
                      getCategoryColor(bill?.category)
                    } hover:opacity-80 transition-opacity`}
                    title={`${bill?.bill_name} - $${bill?.amount}`}
                  >
                    ${bill?.amount}
                  </button>
                ))}
                {dayBills?.length > 3 && (
                  <div className="text-xs text-muted-foreground text-center">
                    +{dayBills?.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-6 pt-4 border-t">
        <h3 className="text-sm font-semibold text-foreground mb-3">Category Legend</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {[
            { category: 'utilities', label: 'Utilities' },
            { category: 'transportation', label: 'Transportation' },
            { category: 'groceries', label: 'Groceries' },
            { category: 'entertainment', label: 'Entertainment' },
            { category: 'healthcare', label: 'Healthcare' },
            { category: 'dining', label: 'Dining' },
            { category: 'shopping', label: 'Shopping' },
            { category: 'housing', label: 'Housing' },
            { category: 'insurance', label: 'Insurance' },
            { category: 'education', label: 'Education' },
            { category: 'other', label: 'Other' }
          ]?.map(({ category, label }) => (
            <div key={category} className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded ${getCategoryColor(category)}`} />
              <span className="text-xs text-muted-foreground">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BillCalendar;